import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { StoryLabelsStateModel } from './story.labels.state.model';
import { 
  AddLabel,
  RemoveLabel,
  SetLabels,
  SelectLabel,
  SetLabel,
  RemoveAllChapterLabels,
  CopyLabels,
  RemoveAllLabels,
  ResetLabels,
  ConvertLabels,
  AddContextLabel,
  EnableContextLabel,
  RemoveContextLabel} from './story.labels.actions';
import { uuid } from 'src/app/shared/uuid';
import { StoryChaptersState } from '../chapters/story.chapters.state';
import { StoryChaptersStateModel } from '../chapters/story.chapters.state.model';
import { LabelsService } from '../../labels.service';
import { LabelRecipe } from '../../models/labels/label-recipe.enum';

import * as _ from 'lodash';
import { FlyToChapterErrandModel } from '../../models/errands/fly-to-chapter-errand.model';
import { FlyToObjectErrandModel } from '../../models/errands/fly-to-object-errand.model';

declare const Human: any;

const initialState = {
  enableContextLabel: true,
  loading: false,
  labels: [],
  contextLabel: null,
  selectedLabel: null
};

@State<StoryLabelsStateModel>({
  name: 'labels',
  defaults: initialState
})
export class StoryLabelsState {
  /**
   * Selectors
   */
  @Selector()
  static getLabels(state: StoryLabelsStateModel) {
    return state.labels;
  }

  @Selector()
  static getSelectedLabel(state: StoryLabelsStateModel) {
    return state.labels.find(label => label.id === state.selectedLabel) || null;
  }

  @Selector()
  static getContextLabel(state: StoryLabelsStateModel) {
    return state.contextLabel;
  }

  @Selector([StoryChaptersState])
  static getChapterLabels(state: StoryLabelsStateModel, storyChaptersState: StoryChaptersStateModel) {
    return state.labels.filter(label => label.chapterId === storyChaptersState.selectedChapter);
  }

  @Selector()
  static getEnableContextLabel(state: StoryLabelsStateModel) {
    return state.enableContextLabel;
  }

  /**
   * Actions
   */
  @Action(SetLabels)
  setLabels(context: StateContext<StoryLabelsStateModel>, action: SetLabels) {
    context.patchState({
      labels: action.labels
    });
  }

  @Action(SetLabel)
  setLabel(context: StateContext<StoryLabelsStateModel>, action: SetLabel) {
    const labels = context.getState().labels;
    const labelIndex = labels.findIndex((_label) => _label.id === action.label.id);
    labels[labelIndex] = { ...action.label };
    
    context.patchState({
      labels: [...labels]
    });
  }

  @Action(AddLabel)
  addLabel(context: StateContext<StoryLabelsStateModel>, action: AddLabel) {
    action.label.id = action.label.id || uuid.idWithPrefix('label');

    const labels = context.getState().labels;
    context.patchState({
      labels: [...labels, {...action.label}]
    });
  }

  @Action(AddContextLabel)
  addContextLabel(context: StateContext<StoryLabelsStateModel>, action: AddContextLabel) {
    const label = {...action.label};
    label.id = 'context-label';

    context.patchState({
      contextLabel: label
    });
  }


  @Action(CopyLabels)
  copyLabels(context: StateContext<StoryLabelsStateModel>, action: CopyLabels) {
    const labels = context.getState().labels;
    const sourceLabels = labels.filter(label => label.chapterId === action.sourceChapter.id);
    const targetLabels = sourceLabels.map(label => {
      return {
        ...(_.cloneDeep(label)),
        id: uuid.idWithPrefix('label'),
        chapterId: action.targetChapter.id
      };
    });

    context.patchState({
      labels: [...labels, ...targetLabels]
    });
  }
  
  @Action(RemoveLabel)
  removeLabel(context: StateContext<StoryLabelsStateModel>, action: RemoveLabel) {
    const labels = context.getState().labels
      .filter((label) => {
        return label.id !== action.label.id;
      });

    context.patchState({
      labels: [...labels]
    });

    Human.scene.markers.delete(action.label.id);
  }

  @Action(RemoveContextLabel)
  RemoveContextLabel(context: StateContext<StoryLabelsStateModel>, action: RemoveContextLabel) {
    context.patchState({
      contextLabel: null
    });

    Human.scene.markers.delete('context-label');
  }

  @Action(RemoveAllLabels) 
  removeAllLabels(context: StateContext<StoryLabelsStateModel>, action: RemoveLabel) {
    const labels = context.getState().labels;

    // Remove labels from the engine
    labels.forEach((label) => {
      Human.scene.markers.delete(label.id);
    });

    // Update application state
    context.patchState({
      labels: []
    });
  }

  @Action(RemoveAllChapterLabels)
  removeAllChapterLabels(context: StateContext<StoryLabelsStateModel>, action: RemoveAllChapterLabels) {
    const labels = context.getState().labels;
    const chapterLabels = labels.filter(label => label.chapterId === action.chapter.id);
    const filteredLabels = labels.filter(label => label.chapterId !== action.chapter.id);

    // Remove labels from the engine
    chapterLabels.forEach((label) => {
      Human.scene.markers.delete(label.id);
    });

    // Update application state
    context.patchState({
      labels: [...filteredLabels]
    });
  }

  @Action(SelectLabel)
  selectLabel(context: StateContext<StoryLabelsStateModel>, action: SelectLabel) {
    context.patchState({
      selectedLabel: action.label ? action.label.id : null
    });
  }

  @Action(ResetLabels)
  resetLabels(context: StateContext<StoryLabelsStateModel>, action: ResetLabels) {
    context.dispatch(new RemoveAllLabels());
    context.dispatch(new RemoveContextLabel());
    context.setState(initialState);
  }

  @Action(ConvertLabels)
  convertLabels(context: StateContext<StoryLabelsStateModel>, action: ConvertLabels) {
    const labels = context.getState().labels;
    action.labels.forEach(label => {
      const index = labels.findIndex(_label => _label.id === label.id);

      switch (action.type) {
        case LabelRecipe.StudioNext:
          labels[index] = this.labelsService.makeDefaultStudioLabel(labels[index]);

          if (label.objectId) {
            labels[index].states[0].errands[0] = new FlyToChapterErrandModel();
            labels[index].states[1].errands[0] = new FlyToObjectErrandModel(label.objectId);  
          }

          break;
        case LabelRecipe.Annotation:
          labels[index] = this.labelsService.makeAnnotationLabel(labels[index]);
          break;
      }
    });

    context.patchState({
      labels: [...labels]
    });
  }

  @Action(EnableContextLabel)
  enableContextLabel(context: StateContext<StoryLabelsStateModel>, action: EnableContextLabel) {
    context.patchState({
      enableContextLabel: action.enable
    });
  }

  constructor(
    private labelsService: LabelsService
  ) { }
}
